/* eslint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from "@hookform/resolvers/yup";
import { Visibility, MoreVert, Delete } from "@mui/icons-material";
import {
    AlertColor,
    Chip,
    Button,
    Popover,
    Switch,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from "@mui/material";
import {
    GridColDef,
    GridRenderCellParams,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import * as yup from "yup";

import { CompleteFilter } from "../../../components/CompleteFilter";
import { applyClientFilters } from "../../../components/CompleteFilter/applyClientFilters";
import InputGeneric from "../../../components/InputGeneric";
import ConfirmModalDefault from "../../../components/Modal/ConfirmModalDefault";
import ModalCreateMonthlyBilling from "../../../components/ModalCreateMonthlyBilling";
import PreloadButton from "../../../components/PreloadButton";
import TableDataGrid from "../../../components/TableDataGrid";
import Toast from "../../../components/Toast";
import { getApiErrorMessage } from "../../../helpers/api/getApiErrorMessage";
import {
    AsaasAccountKey,
    DEFAULT_ASAAS_ACCOUNT_LABELS,
    getAsaasAccountLabel,
    isAsaasAccountKey,
} from "../../../helpers/asaasAccountLabels";
import { formatCurrencyToShow } from "../../../helpers/currency";
import { INVOICE_PAYMENT_METHOD_OPTIONS } from "../../../helpers/invoicePaymentMethods";
import { getStatusChip } from "../../../helpers/statusChip/statusChip";
import { defaultToast } from "../../../helpers/toast/defaultToast";
import { api } from "../../../services/api";
import BillingDetailsModal from "../BillingDetailsModal";
import StartGeneratingInvoicesModal from "../StartGeneratingInvoicesModal";
import { FinancialStyled } from "../styles";
import { CustomersProps } from "../types";
import { CustomerCards } from "./CustomerCards";
import {
    Chips,
    FinacialDetails,
    OptionStyled,
    ContentProposalCustomer,
    ContainerProposalCustomer,
} from "./styles";
import { Account, ProposalByAccountProps } from "./types";

const PAID_INVOICE_STATUS_ID = 4;
const CANCELLED_INVOICE_STATUS_ID = 6;

interface Props {
    currentAccountId: number | undefined;
    myParam: string;
    setCurrentCustomer: Dispatch<SetStateAction<CustomersProps | undefined>>;
    currentCustomer: CustomersProps | undefined;
}

type dueDateFormTypeValues = {
    dueDate: number;
};

const dueDateFormSchema = yup.object().shape({
    dueDate: yup
        .number()
        .required("Campo obrigatório")
        .min(2, "Apenas valores entre 2 e 28")
        .max(28, "Apenas valores entre 2 e 28"),
});

function FinancialContent({
    currentAccountId,
    myParam,
    setCurrentCustomer,
    currentCustomer,
}: Props) {
    const {
        handleSubmit,
        control,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<dueDateFormTypeValues>({
        resolver: yupResolver(dueDateFormSchema),
    });
    const [isLoadingButton, setIsLoadingButton] = useState(false);

    const [loadingTable, setLoadingTable] = useState(true);
    const [queryFilter, setQueryFilter] = useState("");

    // toast configs
    const [openToast, setOpenToast] = useState(false);
    function handleCloseToast() {
        setOpenToast(false);
    }
    const [toastRequisitionResult, setToastRequisitionResult] =
        useState<AlertColor>();
    const [textToast, setTextToast] = useState("");

    // LISTA DE TODAS AS FATURAS
    const [monthlyBillings, setMonthlyBillings] = useState<any>([]);
    const [currentAccount, setCurrentAccount] = useState<Account>({
        id: 0,
        enabled: false,
        invoice_payment_methods: null,
    });
    const [accountEnabled, setAccountEnabled] = useState<boolean>(
        currentAccount?.enabled
    );
    const [invoicePaymentMethods, setInvoicePaymentMethods] =
        useState<string>("");
    const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] =
        useState(false);
    const [isLoadingAsaasAccount, setIsLoadingAsaasAccount] = useState(false);
    const [openCreateMonthlyBillingModal, setOpenCreateMonthlyBillingModal] =
        useState(false);

    // SOMENTE A FATURA SELECIONADA
    const [currentBillingDetails, setCurrentBillingDetails] =
        useState<BillingsDetailsProps | null>(null);

    const [openBillingDetailsModal, setOpenBillingDetailsModal] =
        useState(false);
    const [billingToCancel, setBillingToCancel] =
        useState<BillingsDetailsProps | null>(null);
    const [isLoadingCancelBilling, setIsLoadingCancelBilling] = useState(false);

    const [proposalsByAccount, setProposalsByAccount] =
        useState<ProposalByAccountProps | null>(null);

    const handleCloseBillingDetailsModal = () => {
        setOpenBillingDetailsModal(false);
    };

    const getFinancials = () => {
        if (currentAccountId !== null) {
            setLoadingTable(true);
            api.get(`/account/${currentAccountId}`)
                .then((response) => {
                    setCurrentAccount({
                        id: response.data.response.id,
                        enabled: Boolean(response.data.response.enabled),
                        due_day: response.data.response.due_day,
                        invoice_payment_methods:
                            response.data.response.invoice_payment_methods ??
                            null,
                    });
                    setAccountEnabled(Boolean(response.data.response.enabled));
                    setMonthlyBillings(response.data.response.monthly_billings);
                    setValue("dueDate", response.data.response.due_day);
                    setInvoicePaymentMethods(
                        response.data.response.invoice_payment_methods ?? ""
                    );
                })
                .finally(() => {
                    setLoadingTable(false);
                });
        } else {
            setLoadingTable(false);
        }
    };

    const handleCancelBilling = async () => {
        if (!billingToCancel?.id) {
            return;
        }

        try {
            setIsLoadingCancelBilling(true);
            await api.put(`monthly-billing/cancel/${billingToCancel.id}`);
            defaultToast("success", "Fatura cancelada com sucesso!");
            setBillingToCancel(null);
            getFinancials();
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(error, "Não foi possível cancelar a fatura.")
            );
        } finally {
            setIsLoadingCancelBilling(false);
        }
    };

    const getProposalByAccount = async () => {
        try {
            const responseProposalAccount = await api.get(
                `proposal/find-by-account/${currentAccountId}`
            );
            setProposalsByAccount(responseProposalAccount.data.response);
        } catch (error) {
            defaultToast("success", "Erro ao buscar proposta de cliente");
            console.error(error);
        }
    };

    useEffect(() => {
        getFinancials();
        getProposalByAccount();
    }, []);

    function updateDueDay() {
        setIsLoadingButton(true);

        setLoadingTable(true);
        api.put(`/account/${currentAccountId}`, {
            due_day: getValues("dueDate"),
        })
            .then(() => {
                api.get(`/account/${currentAccountId}`)
                    .then((response) => {
                        setLoadingTable(false);
                        setMonthlyBillings(
                            response.data.response.monthly_billings
                        );
                        setValue("dueDate", response.data.response.due_day);
                        setInvoicePaymentMethods(
                            response.data.response.invoice_payment_methods ?? ""
                        );
                        setOpenToast(true);
                        setToastRequisitionResult("success");
                        setTextToast(response.data.message);
                    })
                    .catch((response) => {
                        setOpenToast(true);
                        setToastRequisitionResult("error");
                        setTextToast(response.response.data.response.name);
                        setIsLoadingButton(false);
                    })
                    .finally(() => {
                        setLoadingTable(false);
                    });
                setIsLoadingButton(false);
            })
            .finally(() => {
                setLoadingTable(false);
            });
    }

    const handleUpdateInvoicePaymentMethods = async (
        event: SelectChangeEvent
    ) => {
        const nextValue = event.target.value;
        setInvoicePaymentMethods(nextValue);

        try {
            setIsLoadingPaymentMethods(true);
            await api.put(`/account/${currentAccountId}`, {
                invoice_payment_methods: nextValue || null,
            });
            defaultToast("success", "Tipo de pagamento atualizado");
            getFinancials();
        } catch (error) {
            defaultToast(
                "error",
                "Não foi possível atualizar o tipo de pagamento"
            );
            setInvoicePaymentMethods(
                currentAccount.invoice_payment_methods ?? ""
            );
        } finally {
            setIsLoadingPaymentMethods(false);
        }
    };

    const currentAsaasAccount = currentCustomer?.asaas_account;
    const asaasAccountValue: AsaasAccountKey = isAsaasAccountKey(
        currentAsaasAccount
    )
        ? currentAsaasAccount
        : "default";

    const handleUpdateAsaasAccount = async (event: SelectChangeEvent) => {
        if (!currentCustomer) {
            return;
        }

        const nextValue = event.target.value as AsaasAccountKey;
        const previousValue = asaasAccountValue;

        if (nextValue === previousValue) {
            return;
        }

        setCurrentCustomer({
            ...currentCustomer,
            asaas_account: nextValue,
        });

        try {
            setIsLoadingAsaasAccount(true);
            await api.put(`customers/${currentCustomer.id}`, {
                name: currentCustomer.name,
                project_name: currentCustomer.project_name,
                email: currentCustomer.email,
                phone: currentCustomer.phone,
                cloud: currentCustomer.cloud,
                holder_id:
                    currentCustomer.holder?.id ??
                    currentCustomer.customer_holder_id,
                holder_name:
                    currentCustomer.holder_name ??
                    currentCustomer.holder?.full_name,
                asaas_account: nextValue,
            });
            defaultToast("success", "Conta Asaas atualizada");

            const responseCustomer = await api.get(`customers/${myParam}`);
            if (responseCustomer.data.response) {
                setCurrentCustomer(responseCustomer.data.response);
            }
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(
                    error,
                    "Não foi possível atualizar a conta Asaas"
                )
            );
            setCurrentCustomer({
                ...currentCustomer,
                asaas_account: previousValue,
            });
        } finally {
            setIsLoadingAsaasAccount(false);
        }
    };

    const [
        openStartGeneratingInvoicesModal,
        setOpenStartGeneratingInvoicesModal,
    ] = useState(false);

    const handleCloseStartGeneratingInvoicesModal = () => {
        setOpenStartGeneratingInvoicesModal(false);
    };

    const filteredRows = applyClientFilters(
        monthlyBillings as unknown as Record<string, unknown>[],
        queryFilter,
        {
            textFields: ["month_reference"],
            dateField: "due_date",
        }
    );

    const columns: GridColDef[] = [
        {
            field: "month_reference",
            headerName: "Mês de referência",
            flex: 10,
        },
        {
            field: "date_reference",
            headerName: "Data de referência",
            flex: 15,
            valueGetter: (params: GridValueGetterParams) =>
                `${params.row.month_and_year_reference}`,
        },
        {
            field: "data_de_fechamento",
            headerName: "Data de fechamento",
            flex: 15,
            valueGetter: (params: GridValueGetterParams) =>
                `${params.row.closing_date.split(" ")[0]}`,
        },
        {
            field: "data_de_vencimento",
            headerName: "Data de Vencimento",
            flex: 15,
            valueGetter: (params: GridValueGetterParams) =>
                `${params.row.due_date.split(" ")[0]}`,
        },
        {
            field: "status",
            headerName: "Status",
            flex: 10,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <div>{getStatusChip(params.row.status.id)}</div>
            ),
        },
        {
            field: "total_amount",
            headerName: "Valor",
            flex: 10,
            renderCell: (params: GridRenderCellParams) => (
                <span>
                    {params.row.status.id === 7 ? (
                        <s>
                            R$ {formatCurrencyToShow(params.row.total_amount)}
                        </s>
                    ) : (
                        <span>
                            R$ {formatCurrencyToShow(params.row.total_amount)}
                        </span>
                    )}
                </span>
            ),
        },
        {
            field: "total_paid",
            headerName: "Valor Pago",
            flex: 10,
            renderCell: (params: GridRenderCellParams) => (
                <span>
                    {params.row.status.id === 7 ? (
                        <s>R$ {formatCurrencyToShow(params.row.total_paid)}</s>
                    ) : (
                        <span>
                            R$ {formatCurrencyToShow(params.row.total_paid)}
                        </span>
                    )}
                </span>
            ),
        },
        {
            field: "ações",
            headerName: "Ações",
            width: 120,
            minWidth: 120,
            maxWidth: 140,
            flex: 0,
            sortable: false,
            align: "center",
            headerAlign: "center",
            disableExport: true,
            cellClassName: "invoice-actions-cell",
            renderCell: (params: GridRenderCellParams) => {
                const statusId = Number(
                    params.row?.status?.id ?? params.row?.status_id
                );
                const canCancel =
                    statusId !== PAID_INVOICE_STATUS_ID &&
                    statusId !== CANCELLED_INVOICE_STATUS_ID;

                return (
                    <div
                        className="containerActionButtonsListTablePagesGlobal"
                        style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            width: "100%",
                        }}
                    >
                        <Visibility
                            className="cursorPointerGlobal"
                            titleAccess="Ver detalhes"
                            onClick={() => {
                                setOpenBillingDetailsModal(true);
                                setCurrentBillingDetails(params.row);
                            }}
                        />
                        {canCancel ? (
                            <Delete
                                className="cursorPointerGlobal"
                                color="error"
                                titleAccess="Cancelar fatura"
                                onClick={() => {
                                    setBillingToCancel(params.row);
                                }}
                            />
                        ) : null}
                    </div>
                );
            },
        },
    ];

    const handleClick = () => {
        api.get(`/account/${currentAccountId}`).then((response) => {
            setLoadingTable(false);
            setCurrentAccount({
                id: response.data.response.id,
                enabled: Boolean(response.data.response.enabled),
            });
            setAccountEnabled(Boolean(response.data.response.enabled));
            setMonthlyBillings(response.data.response.monthly_billings);
            setValue("dueDate", response.data.response.due_day);
        });
    };

    const handleSwitchMonthlyBilling = (accountEnabledStatus: boolean) => {
        setLoadingTable(true);
        api.patch(
            `/account/on-off/${currentAccountId}/${Number(
                accountEnabledStatus
            )}`
        )
            .then(() => {
                defaultToast(
                    "success",
                    `${
                        accountEnabledStatus ? "habilitado" : "desabilitado"
                    } com sucesso!`
                );
            })
            .catch(() => {
                defaultToast(
                    "error",
                    "Não foi possível alterar o status da fatura"
                );
            })
            .finally(() => {
                setLoadingTable(false);
            });
    };

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const handleClickPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    useEffect(() => {
        handleClick();
    }, [accountEnabled]);

    return (
        <>
            <FinancialStyled>
                <div className="containerListTablePagesGlobal">
                    <div className="headerFinancialDetails">
                        <div className="contentRows">
                            <OptionStyled>
                                <Button
                                    aria-describedby={id}
                                    type="button"
                                    color="inherit"
                                    variant="contained"
                                    onClick={(e) => {
                                        handleClickPopover(e);
                                    }}
                                    size="small"
                                >
                                    <MoreVert />
                                    Opções
                                </Button>
                                <Button
                                    type="button"
                                    color="primary"
                                    variant="contained"
                                    onClick={() => {
                                        setOpenCreateMonthlyBillingModal(true);
                                    }}
                                    size="small"
                                    disabled={!currentAccountId}
                                >
                                    Criar faturamento mensal
                                </Button>
                                <Chips>
                                    {currentCustomer?.activate_invoice === 1 ? (
                                        <Chip
                                            label="Assinatura ativa"
                                            color="success"
                                            variant="outlined"
                                        />
                                    ) : (
                                        ""
                                    )}

                                    {currentAccount.enabled === true ? (
                                        <Chip
                                            label="Geração de faturas ativa"
                                            color="success"
                                            variant="outlined"
                                        />
                                    ) : (
                                        <Chip
                                            label={
                                                currentCustomer?.account_id !==
                                                null
                                                    ? "Geração de faturas inativa"
                                                    : "Cliente não possui um conta de faturas"
                                            }
                                            color="default"
                                            variant="outlined"
                                        />
                                    )}
                                </Chips>

                                <Popover
                                    id={id}
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handleClose}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "left",
                                    }}
                                    sx={{
                                        padding: "12px",
                                    }}
                                >
                                    <div
                                        style={{
                                            padding: "10px",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "10px",
                                        }}
                                    >
                                        <div>
                                            <Switch
                                                checked={accountEnabled}
                                                onChange={() => {
                                                    setAccountEnabled(
                                                        !accountEnabled
                                                    );
                                                    handleSwitchMonthlyBilling(
                                                        !accountEnabled
                                                    );
                                                }}
                                            />{" "}
                                            {accountEnabled
                                                ? "Pausar geração e envio de faturas"
                                                : "Retornar geração e envio de faturas"}
                                        </div>
                                        <Divider />
                                        <Button
                                            aria-describedby={id}
                                            type="button"
                                            className="buttonInstall"
                                            color="primary"
                                            variant="contained"
                                            onClick={() => {
                                                setOpenStartGeneratingInvoicesModal(
                                                    true
                                                );
                                            }}
                                            size="small"
                                            disabled={
                                                currentCustomer?.activate_invoice ===
                                                1
                                            }
                                        >
                                            Ativar assinatura do plano na fatura
                                        </Button>
                                    </div>
                                </Popover>
                            </OptionStyled>

                            {proposalsByAccount &&
                                proposalsByAccount?.payment.length > 0 && (
                                    <ContainerProposalCustomer>
                                        <ContentProposalCustomer>
                                            <span>Entrada de pagamento -</span>
                                            <span>
                                                {`R$ ${formatCurrencyToShow(
                                                    proposalsByAccount
                                                        .payment[0].amount
                                                )}`}
                                            </span>
                                        </ContentProposalCustomer>

                                        <div>
                                            <Link
                                                title="Ver detalhes da proposta"
                                                rel="noreferrer"
                                                target="_blank"
                                                to={`/comercial/proposal/proposalDetails?id=${proposalsByAccount?.id}`}
                                            >
                                                Ver detalhes da proposta
                                            </Link>
                                        </div>
                                    </ContainerProposalCustomer>
                                )}
                        </div>
                        {currentAccountId !== null ? (
                            <div className="financialHeaderActions">
                                <form onSubmit={handleSubmit(updateDueDay)}>
                                    <Controller
                                        control={control}
                                        name="dueDate"
                                        render={({
                                            field: { onBlur, value = "", ref },
                                        }) => (
                                            <InputGeneric
                                                inputProps={{
                                                    maxLength: 2,
                                                }}
                                                onChange={(
                                                    event: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    const { value } =
                                                        event.target;

                                                    setValue(
                                                        "dueDate",
                                                        Number(
                                                            value.replace(
                                                                /[^0-9]/g,
                                                                ""
                                                            )
                                                        ) as number
                                                    );
                                                }}
                                                onBlur={onBlur}
                                                value={value}
                                                size="small"
                                                className="inputDueDate"
                                                label="Dia do vencimento"
                                                error={!!errors.dueDate}
                                                ref={ref}
                                                helperText={
                                                    errors?.dueDate?.message
                                                }
                                            />
                                        )}
                                    />
                                    <PreloadButton
                                        colorText="white"
                                        background="var(--confirm)"
                                        loading={isLoadingButton}
                                        text="Atualizar"
                                        type="submit"
                                        sx={{ minWidth: "140px" }}
                                    />
                                </form>
                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 220 }}
                                >
                                    <InputLabel id="invoice-payment-methods-label">
                                        Tipo de pagamento
                                    </InputLabel>
                                    <Select
                                        labelId="invoice-payment-methods-label"
                                        label="Tipo de pagamento"
                                        value={invoicePaymentMethods}
                                        disabled={isLoadingPaymentMethods}
                                        onChange={
                                            handleUpdateInvoicePaymentMethods
                                        }
                                    >
                                        <MenuItem value="">
                                            Padrão (configurações)
                                        </MenuItem>
                                        {INVOICE_PAYMENT_METHOD_OPTIONS.map(
                                            (option) => (
                                                <MenuItem
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 220 }}
                                >
                                    <InputLabel id="asaas-account-label">
                                        Conta Asaas
                                    </InputLabel>
                                    <Select
                                        labelId="asaas-account-label"
                                        label="Conta Asaas"
                                        value={asaasAccountValue}
                                        disabled={
                                            !currentCustomer ||
                                            isLoadingAsaasAccount
                                        }
                                        onChange={handleUpdateAsaasAccount}
                                    >
                                        {(
                                            Object.keys(
                                                DEFAULT_ASAAS_ACCOUNT_LABELS
                                            ) as AsaasAccountKey[]
                                        ).map((account) => (
                                            <MenuItem
                                                key={account}
                                                value={account}
                                            >
                                                {getAsaasAccountLabel(
                                                    account,
                                                    currentCustomer?.asaas_account_labels
                                                )}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        ) : (
                            ""
                        )}
                    </div>
                    <CompleteFilter
                        fields={[
                            {
                                value: "month_reference",
                                label: "Mês de referência",
                            },
                        ]}
                        showDateFilter
                        startDateLabel="Data de Vencimento Início"
                        onSearch={setQueryFilter}
                    />
                    <TableDataGrid
                        rows={filteredRows}
                        columns={columns}
                        loading={loadingTable}
                        sx={{
                            "& .invoice-actions-cell": {
                                overflow: "visible",
                            },
                            "& .MuiDataGrid-cell": {
                                overflow: "visible",
                            },
                        }}
                    />
                </div>
            </FinancialStyled>
            <BillingDetailsModal
                getFinancial={getFinancials}
                openBillingDetailsModal={openBillingDetailsModal}
                handleCloseBillingDetailsModal={handleCloseBillingDetailsModal}
                currentBillingDetails={currentBillingDetails}
                handleClick={handleClick}
                holder={{
                    phone: currentCustomer?.phone,
                }}
                setOpenBillingDetailsModal={setOpenBillingDetailsModal}
            />
            <StartGeneratingInvoicesModal
                setCurrentCustomer={setCurrentCustomer}
                openStartGeneratingInvoicesModal={
                    openStartGeneratingInvoicesModal
                }
                handleCloseStartGeneratingInvoicesModal={
                    handleCloseStartGeneratingInvoicesModal
                }
                myParam={myParam ?? ""}
            />
            <ModalCreateMonthlyBilling
                visible={openCreateMonthlyBillingModal}
                setVisible={setOpenCreateMonthlyBillingModal}
                accountId={currentAccountId}
                getFinancial={getFinancials}
                accountPaymentMethods={invoicePaymentMethods || null}
            />
            <ConfirmModalDefault
                loading={isLoadingCancelBilling}
                isOpen={Boolean(billingToCancel)}
                handleClose={() => setBillingToCancel(null)}
                onClick={() => {
                    handleCancelBilling();
                }}
                buttonColor="error"
                title="Deseja realmente cancelar esta fatura?"
            />
            <FinacialDetails>
                <CustomerCards cards={currentCustomer?.cards ?? []} />
            </FinacialDetails>
            <Toast
                open={openToast}
                onClose={() => handleCloseToast()}
                severity={toastRequisitionResult}
                text={textToast}
            />
        </>
    );
}
export default FinancialContent;
